import React, { useEffect, useMemo, useState } from 'react';
import BoardItem from '@cloudscape-design/board-components/board-item';
import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Alert, Box, Button, ButtonDropdown, CollectionPreferences, Header, Link, Modal,
  Pagination, Select, SpaceBetween, Table, TextFilter,
} from '@cloudscape-design/components';
import { apiFetch } from '../../../auth/apiClient';
import FinanceExpensesHelp from '../../../helpPanelContents/financeExpensesHelp';
import { formatCurrencyDisplay } from '../../../utils/currencyFormat';
import { boardItemI18nStrings } from './common';
import ExpenseForm from './ExpenseForm';
import './expenses.css';

const { currentFiscalYear, fiscalYearForDate } = require('../../../lib/budgetFiscal');
const preferenceKey = 'path.expenses.table.v1';
const columnOptions = [
  { id: 'date', label: 'Date', alwaysVisible: true },
  { id: 'description', label: 'Description', alwaysVisible: true },
  { id: 'budgetPotName', label: 'Budget pot' }, { id: 'amount', label: 'Amount' },
  { id: 'repeat', label: 'Repeat' },
];
const normalizePreferences = (saved = {}) => {
  const display = Array.isArray(saved.contentDisplay) ? saved.contentDisplay : [];
  const known = new Set(columnOptions.map(column => column.id));
  const order = [...new Set([...display.map(column => column?.id).filter(id => known.has(id)), ...known])];
  return {
    pageSize: [10, 25, 50].includes(saved.pageSize) ? saved.pageSize : 25,
    contentDisplay: order.map(id => ({
      id, visible: columnOptions.find(column => column.id === id).alwaysVisible
        || display.find(column => column?.id === id)?.visible !== false,
    })),
    widths: Object.fromEntries(Object.entries(saved.widths || {}).filter(([key, value]) =>
      [...known, 'actions'].includes(key) && Number.isFinite(value) && value >= 80)),
  };
};
const loadPreferences = () => {
  try { return normalizePreferences(JSON.parse(localStorage.getItem(preferenceKey) || '{}')); }
  catch (_) { return normalizePreferences(); }
};
const yearLabel = value => value.slice(0, 4) + '/' + value.slice(-2);
const repeatLabel = item => {
  if (!item.recurring) return 'One-off';
  const interval = Number(item.interval) || 1;
  if (interval === 1) return { weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' }[item.frequency] || 'Recurring';
  return 'Every ' + interval + ' ' + ({ weekly: 'weeks', monthly: 'months', yearly: 'years' }[item.frequency] || 'periods');
};
const initialYear = () => {
  const requested = new URLSearchParams(window.location.search).get('fiscalYear');
  return /^\d{4}-\d{4}$/.test(requested || '') && Number(requested.slice(5)) === Number(requested.slice(0, 4)) + 1
    ? requested : currentFiscalYear();
};
async function readResponse(response, fallback) {
  let body = {};
  try { body = await response.json(); } catch (_) { /* The response may have no JSON body. */ }
  if (!response.ok) throw new Error(response.status === 403 ? 'You do not have access to manage expenses.'
    : response.status === 409 ? 'This expense has changed. Close this form, reload the expenses and review it before trying again.'
      : body.message || fallback);
  return body;
}

export default function ExpensesWidget({ actions, toggleHelpPanel }) {
  const [year, setYear] = useState(initialYear);
  const [potFamily, setPotFamily] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [preferences, setPreferences] = useState(loadPreferences);
  const [form, setForm] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState('');

  useEffect(() => {
    try { localStorage.setItem(preferenceKey, JSON.stringify(preferences)); } catch (_) { /* Storage is optional. */ }
  }, [preferences]);
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null);
    (async () => {
      try {
        const response = await apiFetch('/api/finance/expenses?fiscalYear=' + encodeURIComponent(year), { signal: controller.signal });
        const payload = await readResponse(response, 'Unable to load expenses. Please try again.');
        if (!cancelled) setData(payload);
      } catch (failure) { if (!cancelled) setError(failure.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [year, refresh]);

  const potChoices = useMemo(() => (data?.potChoices || []).map(option => ({ ...option, value: String(option.value) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'en-CA', { sensitivity: 'base', numeric: true })), [data]);
  const filterChoices = useMemo(() => [{ value: '', label: 'All budget pots' },
    ...potChoices.map(option => ({ ...option, value: option.familyKey || option.value }))], [potChoices]);
  const years = useMemo(() => [...new Set([year, currentFiscalYear(), ...(data?.fiscalYears || [])])]
    .sort().reverse().map(value => ({ value, label: yearLabel(value) + (value === currentFiscalYear() ? ' (current)' : '') })), [year, data]);
  const filteredItems = useMemo(() => (data?.items || []).filter(item => !potFamily
    || (item.familyKey || String(item.budgetPotId)) === potFamily), [data, potFamily]);
  const { items, collectionProps, filterProps, paginationProps } = useCollection(filteredItems, {
    filtering: {
      fields: ['description', 'budgetPotName', 'date'],
      empty: <Box padding="m">No expenses for this fiscal year and budget pot.</Box>,
      noMatch: <Box padding="m">No expenses match your search.</Box>,
    },
    sorting: { defaultState: { sortingColumn: { sortingField: 'date' }, isDescending: true } },
    pagination: { pageSize: preferences.pageSize },
  });
  const save = async payload => {
    if (busy) return;
    setBusy(true); setWriteError('');
    try {
      const response = await apiFetch('/api/finance/expenses' + (form.item ? '/' + encodeURIComponent(form.item.id) : ''), {
        method: form.item ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      await readResponse(response, 'Unable to save the expense. Please try again.');
      setForm(null); setPotFamily('');
      const nextYear = fiscalYearForDate(payload.date);
      if (nextYear !== year) setYear(nextYear);
      else setRefresh(value => value + 1);
    } catch (failure) { setWriteError(failure.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true); setWriteError('');
    try {
      const response = await apiFetch('/api/finance/expenses/' + encodeURIComponent(deleting.id), {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope: 'this', version: deleting.version }),
      });
      await readResponse(response, 'Unable to delete the expense. Please try again.');
      setDeleting(null); setRefresh(value => value + 1);
    } catch (failure) { setWriteError(failure.message); }
    finally { setBusy(false); }
  };
  const definitions = [
    { id: 'date', header: 'Date', sortingField: 'date', width: 145, minWidth: 115, cell: item => item.date },
    { id: 'description', header: 'Description', sortingField: 'description', width: 330, minWidth: 180, cell: item => item.description },
    { id: 'budgetPotName', header: 'Budget pot', sortingField: 'budgetPotName', width: 300, minWidth: 180, cell: item => item.budgetPotName },
    { id: 'amount', header: 'Amount', sortingField: 'amount', width: 155, minWidth: 120,
      cell: item => <Box textAlign="right">{formatCurrencyDisplay(item.amount)}</Box> },
    { id: 'repeat', header: 'Repeat', width: 135, minWidth: 110, cell: repeatLabel },
    { id: 'actions', header: '', width: 90, minWidth: 80, cell: item => <ButtonDropdown
      ariaLabel={'Actions for ' + item.description + ', ' + item.date} variant="icon" expandToViewport
      items={[{ id: 'edit', text: 'Edit' }, { id: 'delete', text: 'Delete' }]}
      onItemClick={({ detail }) => {
        setWriteError('');
        if (detail.id === 'edit') setForm({ item });
        if (detail.id === 'delete') setDeleting(item);
      }} /> },
  ].filter(column => column.id === 'actions' || preferences.contentDisplay.some(item => item.id === column.id && item.visible))
    .sort((left, right) => {
      const position = column => column.id === 'actions' ? preferences.contentDisplay.length
        : preferences.contentDisplay.findIndex(item => item.id === column.id);
      return position(left) - position(right);
    })
    .map(column => ({ ...column, width: preferences.widths[column.id] || column.width }));

  return <BoardItem i18nStrings={boardItemI18nStrings}
    header={<Header variant="h2" info={<Link variant="info" onFollow={event => {
      event.preventDefault(); toggleHelpPanel?.(<FinanceExpensesHelp />, 'Expenses', FinanceExpensesHelp.aiContext);
    }}>Info</Link>}
      actions={<div className="finance-expenses-actions">
        <Select ariaLabel="Expense fiscal year" options={years} selectedOption={years.find(option => option.value === year)}
          onChange={({ detail }) => setYear(detail.selectedOption.value)} />
        <div className="finance-expenses-pot-filter"><Select ariaLabel="Filter expenses by budget pot" filteringType="auto"
          options={filterChoices} selectedOption={filterChoices.find(option => option.value === potFamily) || filterChoices[0]}
          onChange={({ detail }) => setPotFamily(detail.selectedOption.value)} /></div>
        <Button variant="primary" disabled={loading || !potChoices.length} onClick={() => { setWriteError(''); setForm({ item: null }); }}>Add expense</Button>
      </div>}>Expenses</Header>}
    settings={<ButtonDropdown ariaLabel="Expenses widget settings" variant="icon" items={[{ id: 'remove', text: 'Remove widget' }]}
      onItemClick={({ detail }) => { if (detail.id === 'remove') actions.removeItem(); }} />}>
    <div>
    <SpaceBetween size="s">
      {!!error && <Alert type="error" action={<Button onClick={() => setRefresh(value => value + 1)}>Retry</Button>}>{error}</Alert>}
      <Table {...collectionProps} variant="embedded" items={items} columnDefinitions={definitions} trackBy="id"
        resizableColumns stickyHeader enableKeyboardNavigation loading={loading} loadingText="Loading expenses"
        ariaLabels={{ tableLabel: 'Expenses', resizerRoleDescription: 'Column resize handle' }}
        filter={<TextFilter {...filterProps} filteringPlaceholder="Find expenses" filteringAriaLabel="Find expenses" />}
        pagination={<Pagination {...paginationProps} ariaLabels={{ nextPageLabel: 'Next page', previousPageLabel: 'Previous page', pageLabel: page => 'Page ' + page }} />}
        onColumnWidthsChange={({ detail }) => setPreferences(previous => normalizePreferences({ ...previous,
          widths: { ...previous.widths, ...Object.fromEntries(detail.widths.map((width, index) => [definitions[index].id, width])) } }))}
        preferences={<CollectionPreferences title="Table preferences" confirmLabel="Confirm" cancelLabel="Cancel"
          preferences={preferences} onConfirm={({ detail }) => setPreferences(normalizePreferences({ ...preferences, ...detail }))}
          pageSizePreference={{ title: 'Page size', options: [10, 25, 50].map(value => ({ value, label: value + ' expenses' })) }}
          contentDisplayPreference={{ title: 'Columns', options: columnOptions }} />} />
    </SpaceBetween>
    {form && <ExpenseForm item={form.item} potChoices={potChoices}
      initialPot={potChoices.find(option => (option.familyKey || option.value) === potFamily)?.value}
      busy={busy} error={writeError} onDismiss={() => setForm(null)} onSave={save} />}
    {deleting && <Modal visible header="Delete expense" closeAriaLabel="Close delete expense"
      onDismiss={() => { if (!busy) setDeleting(null); }}
      footer={<Box float="right"><SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" disabled={busy} onClick={() => setDeleting(null)}>Cancel</Button>
        <Button variant="primary" loading={busy} onClick={remove}>Delete</Button>
      </SpaceBetween></Box>}>
      <SpaceBetween size="m">
        {!!writeError && <Alert type="error">{writeError}</Alert>}
        <Box>Delete {deleting.description}?</Box>
      </SpaceBetween>
    </Modal>}
    </div>
  </BoardItem>;
}
